import { useState } from 'react'

/**
 * Job listings data – Summer Internships 2025
 */
const JOB_CATEGORIES = [
  {
    name: 'Summer Internships 2025',
    jobs: [
      {
        title: '2D Game Art & Animation Intern',
        teams: 1,
        location: 'Remote',
        timezone: 'IST',
        description: 'Join us this summer to help bring our 2D worlds to life! As a 2D Game Art Intern, you will get hands-on experience designing vibrant sprites, backgrounds, and animations. You will learn how art transitions from the drawing board directly into the Unity engine, gaining valuable insight into the indie game art pipeline.',
        requirements: [
          'Familiarity with 2D digital art software (e.g., Photoshop, Aseprite, Illustrator, or Procreate)',
          'A portfolio showcasing 2D illustrations, pixel art, or animations',
          'Basic familiarity with the Unity Editor is a plus, but a strong desire to learn is required',
          'Create 2D assets including characters, environments, and UI elements',
          'Develop sprite sheets and frame-by-frame or skeletal animations',
          'Import and configure artwork within Unity, working with the Sprite Editor and Animator',
          'Collaborate with the team to ensure the art style remains consistent and game-ready'
        ]
      },
      {
        title: 'Unity 2D Level Design Intern',
        teams: 1,
        location: 'Remote',
        timezone: 'IST',
        description: 'Spend your summer building the playgrounds where our players will jump, dash, and explore. As a 2D Level Design Intern, you will learn how to craft engaging levels using Unity’s 2D toolset. You will focus on player flow, obstacle placement, and environmental storytelling to create memorable gameplay experiences.',
        requirements: [
          'Basic understanding of the Unity Editor and navigating 2D scenes',
          'A strong passion for 2D game genres (platformers, puzzles, RPGs) and an understanding of what makes their levels fun',
          'Any personal projects or game jam games showcasing level design are a major bonus',
          'Design and assemble 2D levels using Unity’s Tilemap system',
          'Block out initial level concepts (grayboxing) and iterate based on playtest feedback',
          'Set up 2D colliders, trigger zones, and spawn points within the scenes',
          'Work with the programming and art teams to integrate mechanics and visual assets seamlessly'
        ]
      },
      {
        title: 'Unity 2D Programming Intern',
        teams: 1,
        location: 'Remote',
        timezone: 'IST',
        description: 'Dive into the code that makes games tick! As a Unity 2D Programming Intern, you will write C# scripts to bring gameplay mechanics to life. This summer role is perfect for a coder looking to gain practical experience with Unity\'s 2D physics, UI scripting, and player controllers in a real-world development environment.',
        requirements: [
          'Solid foundational knowledge of C# or a similar object-oriented programming language',
          'Prior exposure to Unity (school projects, self-taught tutorials, or game jams)',
          'A logical mindset with good problem-solving and debugging skills',
          'Write and debug clean C# code for 2D gameplay systems (e.g., movement, combat, inventory)',
          'Work extensively with Unity components like Rigidbody2D, BoxCollider2D, and the new Input System',
          'Assist in optimizing game performance and squashing bugs',
          'Prototype small, experimental 2D mechanics to test new ideas'
        ]
      },
      {
        title: '2D Game Design & Narrative Intern',
        teams: 1,
        location: 'Remote',
        timezone: 'IST',
        description: 'Help us craft the mechanics and stories that keep players hooked. As a Game Design & Narrative Intern, you will spend your summer balancing game variables, writing dialogue, and documenting features. You will learn how to merge storytelling with 2D gameplay to create a cohesive and engaging player journey.',
        requirements: [
          'Strong creative writing and clear technical communication skills',
          'A deep appreciation for narrative-driven or mechanic-heavy 2D games',
          'Detail-oriented mindset with an interest in game balancing and player psychology',
          'Assist in writing in-game text, character dialogue, and lore',
          'Help maintain and update Game Design Documents (GDD) for the team',
          'Playtest internal builds to balance difficulty, tweak variables, and provide actionable feedback',
          'Design and document new 2D puzzles, quests, or gameplay loops'
        ]
      }
    ]
  }
];

const TOTAL_POSITIONS = JOB_CATEGORIES.reduce((acc, cat) => acc + cat.jobs.length, 0)

/**
 * Isometric Office SVG Component
 */
function IsometricOffice() {
  return (
    <div className="relative w-full h-48 bg-gradient-to-b from-orange-600 to-orange-700 overflow-hidden rounded-t-lg">
      <svg className="absolute inset-0 w-full h-full opacity-30" viewBox="0 0 100 100" preserveAspectRatio="none">
        <pattern id="zigzag" width="10" height="10" patternUnits="userSpaceOnUse">
          <path d="M0 5 L5 0 L10 5 L5 10 Z" fill="none" stroke="#8B4513" strokeWidth="0.5"/>
        </pattern>
        <rect width="100" height="100" fill="url(#zigzag)"/>
      </svg>
      
      <svg className="absolute inset-0 w-full h-full" viewBox="0 0 400 200">
        <g transform="translate(30, 60)">
          <polygon points="0,30 40,10 80,30 40,50" fill="#d4a574" stroke="#8B4513" strokeWidth="1"/>
          <polygon points="0,30 0,60 40,80 40,50" fill="#c49464" stroke="#8B4513" strokeWidth="1"/>
          <polygon points="40,50 40,80 80,60 80,30" fill="#b48454" stroke="#8B4513" strokeWidth="1"/>
          <polygon points="15,35 40,25 65,35 40,45" fill="#5c3d2e" stroke="#3d2619" strokeWidth="0.5"/>
          <rect x="35" y="20" width="10" height="8" fill="#1a1a2e" stroke="#333" strokeWidth="0.5"/>
          <rect x="36" y="21" width="8" height="6" fill="#7aa2f7"/>
          <circle cx="40" cy="40" r="5" fill="#f5f5f5"/>
          <circle cx="40" cy="35" r="3" fill="#f5f5f5"/>
        </g>
        
        <g transform="translate(150, 40)">
          <polygon points="0,30 40,10 80,30 40,50" fill="#d4a574" stroke="#8B4513" strokeWidth="1"/>
          <polygon points="0,30 0,60 40,80 40,50" fill="#c49464" stroke="#8B4513" strokeWidth="1"/>
          <polygon points="40,50 40,80 80,60 80,30" fill="#b48454" stroke="#8B4513" strokeWidth="1"/>
          <polygon points="15,35 40,25 65,35 40,45" fill="#5c3d2e" stroke="#3d2619" strokeWidth="0.5"/>
          <rect x="35" y="20" width="10" height="8" fill="#1a1a2e" stroke="#333" strokeWidth="0.5"/>
          <rect x="36" y="21" width="8" height="6" fill="#9ece6a"/>
          <circle cx="40" cy="40" r="5" fill="#ff9e64"/>
          <circle cx="40" cy="35" r="3" fill="#ff9e64"/>
          <polygon points="37,32 40,28 43,32" fill="#ff9e64"/>
        </g>
        
        <g transform="translate(270, 60)">
          <polygon points="0,30 40,10 80,30 40,50" fill="#d4a574" stroke="#8B4513" strokeWidth="1"/>
          <polygon points="0,30 0,60 40,80 40,50" fill="#c49464" stroke="#8B4513" strokeWidth="1"/>
          <polygon points="40,50 40,80 80,60 80,30" fill="#b48454" stroke="#8B4513" strokeWidth="1"/>
          <polygon points="15,35 40,25 65,35 40,45" fill="#5c3d2e" stroke="#3d2619" strokeWidth="0.5"/>
          <rect x="35" y="20" width="10" height="8" fill="#1a1a2e" stroke="#333" strokeWidth="0.5"/>
          <rect x="36" y="21" width="8" height="6" fill="#bb9af7"/>
          <circle cx="40" cy="40" r="5" fill="#1a1a2e"/>
          <circle cx="40" cy="35" r="3" fill="#f5f5f5"/>
        </g>
        
        <g transform="translate(120, 110)">
          <rect x="0" y="10" width="8" height="12" fill="#8b5a2b"/>
          <circle cx="4" cy="5" r="8" fill="#4a7c59"/>
        </g>
        <g transform="translate(280, 130)">
          <rect x="0" y="10" width="8" height="12" fill="#8b5a2b"/>
          <circle cx="4" cy="5" r="8" fill="#5a9c69"/>
        </g>
        
        <g transform="translate(220, 120)">
          <rect x="0" y="5" width="12" height="20" fill="#7dcfff" opacity="0.5"/>
          <rect x="2" y="0" width="8" height="8" fill="#4a5568"/>
        </g>
      </svg>
      
      <div className="absolute inset-0 flex flex-col items-center justify-center text-center">
        <h1 className="text-2xl font-bold text-white drop-shadow-lg">Summer Internships 2025</h1>
        <p className="text-white/90 mt-2">
          We're looking for <span className="font-bold">{TOTAL_POSITIONS} interns</span> to join us.
        </p>
      </div>
    </div>
  )
}

/**
 * Icons
 */
const SearchIcon = () => (
  <svg className="w-4 h-4 text-slate-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
  </svg>
)

const LocationIcon = () => (
  <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
    <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5a2.5 2.5 0 010-5 2.5 2.5 0 010 5z"/>
  </svg>
)

const ClockIcon = () => (
  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
  </svg>
)

/**
 * Main WorkHere Component with Apply button enabled
 */
function WorkHere() {
  const [searchQuery, setSearchQuery] = useState('')
  const [selectedJob, setSelectedJob] = useState(JOB_CATEGORIES[0].jobs[0])
  const [selectedCategory, setSelectedCategory] = useState(null)
  const [showApplyModal, setShowApplyModal] = useState(false)
  const [applicationData, setApplicationData] = useState({
    name: '',
    email: '',
    portfolio: '',
    experience: '',
    coverLetter: '',
  })
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [submitStatus, setSubmitStatus] = useState(null)

  // Filter jobs
  const filteredCategories = JOB_CATEGORIES.map(category => ({
    ...category,
    jobs: category.jobs.filter(job => 
      job.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      category.name.toLowerCase().includes(searchQuery.toLowerCase())
    )
  })).filter(category => 
    selectedCategory ? category.name === selectedCategory : true
  ).filter(category => category.jobs.length > 0)

  // Handle application submission
  const handleApply = async (e) => {
    e.preventDefault()
    setIsSubmitting(true)
    setSubmitStatus(null)

    // Simulate network request
    await new Promise(resolve => setTimeout(resolve, 1500))
    
    // Here you would normally send data to your backend or email service
    console.log('Application submitted:', { ...applicationData, role: selectedJob.title })
    
    setSubmitStatus('success')
    setApplicationData({
      name: '',
      email: '',
      portfolio: '',
      experience: '',
      coverLetter: '',
    })
    setTimeout(() => {
      setShowApplyModal(false)
      setSubmitStatus(null)
    }, 2000)
    
    setIsSubmitting(false)
  }

  const handleModalClose = () => {
    if (!isSubmitting) {
      setShowApplyModal(false)
      setSubmitStatus(null)
    }
  }

  return (
    <div className="h-full bg-slate-950 overflow-auto">
      <IsometricOffice />

      <div className="flex flex-col md:flex-row">
        {/* Sidebar */}
        <div className="md:w-64 border-b md:border-b-0 md:border-r border-slate-800 p-4">
          <div className="relative mb-4">
            <div className="absolute left-3 top-1/2 -translate-y-1/2">
              <SearchIcon />
            </div>
            <input
              type="text"
              placeholder="Search roles..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full bg-slate-800 border border-slate-700 rounded-lg pl-10 pr-4 py-2 text-sm text-slate-300 placeholder-slate-500 focus:outline-none focus:border-accent-blue"
            />
          </div>

          <div className="flex md:block overflow-x-auto md:overflow-x-visible gap-2 md:gap-0 pb-2 md:pb-0">
            {filteredCategories.map((category) => (
              <div key={category.name} className="mb-4 flex-shrink-0 md:flex-shrink">
                <h3 className="text-xs font-medium text-slate-500 uppercase tracking-wide mb-2 whitespace-nowrap">
                  {category.name}
                </h3>
                <div className="flex md:block gap-2 md:gap-0">
                  {category.jobs.map((job) => (
                    <button
                      key={job.title}
                      onClick={() => setSelectedJob(job)}
                      className={`text-left p-3 rounded-lg mb-1 transition-colors whitespace-nowrap md:whitespace-normal md:w-full ${
                        selectedJob?.title === job.title 
                          ? 'bg-slate-800 border border-slate-600' 
                          : 'hover:bg-slate-800/50 bg-slate-800/30 md:bg-transparent'
                      }`}
                    >
                      <div className="font-medium text-white text-sm">{job.title}</div>
                      <div className="text-xs text-slate-500">
                        {job.teams} {job.teams === 1 ? 'team' : 'teams'}
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Job Details */}
        <div className="flex-1 p-4 md:p-6">
          {selectedJob && (
            <>
              <h2 className="text-xl md:text-2xl font-bold text-white mb-4">{selectedJob.title}</h2>
              
              <div className="inline-block bg-slate-800 rounded-lg px-3 md:px-4 py-2 mb-4 md:mb-6">
                <span className="text-slate-300 text-xs md:text-sm">
                  {selectedJob.teams} small {selectedJob.teams === 1 ? 'team is' : 'teams are'} hiring for this role
                </span>
              </div>

              <div className="flex items-center gap-8 mb-6">
                <div className="flex items-center gap-3">
                  <div className="text-accent-yellow">
                    <LocationIcon />
                  </div>
                  <div>
                    <div className="text-xs text-slate-500">Location</div>
                    <div className="text-white font-medium">{selectedJob.location}</div>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <div className="text-accent-blue">
                    <ClockIcon />
                  </div>
                  <div>
                    <div className="text-xs text-slate-500">Timezone(s)</div>
                    <div className="text-white font-medium">{selectedJob.timezone}</div>
                  </div>
                </div>
              </div>

              <div className="mb-6">
                <h3 className="text-lg font-semibold text-white mb-2">About the role</h3>
                <p className="text-slate-400">{selectedJob.description}</p>
              </div>

              <div className="mb-6">
                <h3 className="text-lg font-semibold text-white mb-2">What you'll do & what we're looking for</h3>
                <ul className="space-y-2">
                  {selectedJob.requirements.map((req, i) => (
                    <li key={i} className="flex items-start gap-2 text-slate-400">
                      <span className="w-1.5 h-1.5 rounded-full bg-accent-green mt-1.5"></span>
                      {req}
                    </li>
                  ))}
                </ul>
              </div>

              {/* ✅ Apply Button - NOW ACTIVE */}
              <button 
                onClick={() => setShowApplyModal(true)}
                className="w-full md:w-auto px-6 py-3 bg-accent-blue hover:bg-accent-blue/80 active:bg-accent-blue/70 text-white font-medium rounded-lg transition-colors"
              >
                Apply for this role →
              </button>
            </>
          )}
        </div>

        {/* Right Sidebar */}
        <div className="hidden lg:block w-72 border-l border-slate-800 p-4">
          <h3 className="text-lg font-semibold text-white mb-4">About the small teams</h3>
          
          <div className="space-y-3">
            <div className="bg-slate-800/50 rounded-lg p-4 border border-slate-700">
              <div className="flex items-center justify-between mb-2">
                <span className="font-medium text-white">Core Game Team</span>
                <svg className="w-4 h-4 text-slate-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </div>
              <p className="text-sm text-slate-500">Building Project Pancake and core gameplay systems</p>
            </div>
            
            <div className="bg-slate-800/50 rounded-lg p-4 border border-slate-700">
              <div className="flex items-center justify-between mb-2">
                <span className="font-medium text-white">Art & Animation</span>
                <svg className="w-4 h-4 text-slate-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </div>
              <p className="text-sm text-slate-500">Creating visual magic for all our games</p>
            </div>
            
            <div className="bg-slate-800/50 rounded-lg p-4 border border-slate-700">
              <div className="flex items-center justify-between mb-2">
                <span className="font-medium text-white">Platform Team</span>
                <svg className="w-4 h-4 text-slate-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </div>
              <p className="text-sm text-slate-500">Infrastructure and backend services</p>
            </div>
          </div>

          <div className="mt-6 pt-4 border-t border-slate-800">
            <h4 className="text-sm font-medium text-slate-400 mb-3">Why join us?</h4>
            <ul className="space-y-2 text-sm text-slate-500">
              <li className="flex items-center gap-2">
                <span className="text-accent-green">✓</span> Fully remote
              </li>
              <li className="flex items-center gap-2">
                <span className="text-accent-green">✓</span> Flexible hours
              </li>
              <li className="flex items-center gap-2">
                <span className="text-accent-green">✓</span> Work on cool games
              </li>
              <li className="flex items-center gap-2">
                <span className="text-accent-green">✓</span> Small team, big impact
              </li>
            </ul>
          </div>
        </div>
      </div>

      {/* ✅ Application Modal - FULLY ADDED */}
      {showApplyModal && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4" onClick={handleModalClose}>
          <div className="bg-slate-900 rounded-xl border border-slate-700 w-full max-w-lg max-h-[90vh] overflow-auto" onClick={(e) => e.stopPropagation()}>
            <div className="p-4 border-b border-slate-800 flex justify-between items-center">
              <h3 className="text-xl font-bold text-white">Apply for {selectedJob?.title}</h3>
              {!isSubmitting && (
                <button onClick={handleModalClose} className="text-slate-400 hover:text-white text-xl">✕</button>
              )}
            </div>
            
            <form onSubmit={handleApply} className="p-4 space-y-4">
              {submitStatus === 'success' && (
                <div className="bg-accent-green/20 border border-accent-green rounded-lg p-3 text-center">
                  <p className="text-accent-green">Application sent successfully! We'll be in touch.</p>
                </div>
              )}
              
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-1">Full Name *</label>
                <input
                  type="text"
                  required
                  value={applicationData.name}
                  onChange={(e) => setApplicationData({...applicationData, name: e.target.value})}
                  className="w-full bg-slate-800 border border-slate-700 rounded-lg px-4 py-2 text-white focus:outline-none focus:border-accent-blue"
                  placeholder="Your name"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-1">Email *</label>
                <input
                  type="email"
                  required
                  value={applicationData.email}
                  onChange={(e) => setApplicationData({...applicationData, email: e.target.value})}
                  className="w-full bg-slate-800 border border-slate-700 rounded-lg px-4 py-2 text-white focus:outline-none focus:border-accent-blue"
                  placeholder="you@example.com"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-1">Portfolio / GitHub / LinkedIn URL</label>
                <input
                  type="url"
                  value={applicationData.portfolio}
                  onChange={(e) => setApplicationData({...applicationData, portfolio: e.target.value})}
                  className="w-full bg-slate-800 border border-slate-700 rounded-lg px-4 py-2 text-white focus:outline-none focus:border-accent-blue"
                  placeholder="Link to your work"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-1">Relevant Experience (years / projects)</label>
                <textarea
                  rows={2}
                  value={applicationData.experience}
                  onChange={(e) => setApplicationData({...applicationData, experience: e.target.value})}
                  className="w-full bg-slate-800 border border-slate-700 rounded-lg px-4 py-2 text-white focus:outline-none focus:border-accent-blue"
                  placeholder="Tell us about your background with Unity, C#, or art tools..."
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-1">Why do you want to join MudgalOverseas?</label>
                <textarea
                  rows={3}
                  value={applicationData.coverLetter}
                  onChange={(e) => setApplicationData({...applicationData, coverLetter: e.target.value})}
                  className="w-full bg-slate-800 border border-slate-700 rounded-lg px-4 py-2 text-white focus:outline-none focus:border-accent-blue"
                  placeholder="Your motivation and what you'd bring to the team..."
                />
              </div>
              
              <div className="flex gap-3 pt-2">
                <button
                  type="button"
                  onClick={handleModalClose}
                  disabled={isSubmitting}
                  className="flex-1 px-4 py-2 border border-slate-600 rounded-lg text-slate-300 hover:bg-slate-800 disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="flex-1 bg-accent-blue hover:bg-accent-blue/80 text-white font-medium py-2 rounded-lg disabled:opacity-50 transition-colors"
                >
                  {isSubmitting ? 'Sending...' : 'Submit Application'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  )
}

export default WorkHere